import { useCallback, useMemo, useState } from 'react';
import createContext from 'constate';
import useDeliveryFee from '../hooks/useDeliveryFee';
import useDeliveryMethod from '../hooks/useDeliveryMethod';
import { useLocation } from './LocationContext';
import { useCartStore } from './cart/CartStore';
import { shallow } from 'zustand/shallow';
import { PaymentMethodDto } from '../../generated-api';

const [CheckoutContextProvider, useCheckout] = createContext(() => {
  const { location } = useLocation();
  const getDefaultVatType = useCallback((): 'EAT_IN' | 'TAKEAWAY' => {
    return location && location.isTakeAwayPreselected ? 'TAKEAWAY' : 'EAT_IN';
  }, [location]);

  const { comment, vatType, saveComment, saveVatType } = useCartStore(
    (x) => ({
      comment: x.cart.comment,
      vatType: x.cart.vatType,
      saveComment: x.saveComment,
      saveVatType: x.saveVatType,
    }),
    shallow
  );

  const [tableNumber, setTableNumber] = useState<string>();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodDto>();
  const [dynamicFieldValues, setDynamicFieldValues] = useState<{ [key: string]: string }>({});

  const [sidebarContent, setSidebarContent] = useState<
    'CART' | 'PAYMENT' | 'CREATE_TABLE' | 'SET_TABLE_NUMBER' | 'SELECT_TABLE'
  >('CART');

  const deliveryMethod = useDeliveryMethod(vatType, tableNumber);

  const resetVatType = useCallback(() => {
    saveVatType(getDefaultVatType());
  }, [getDefaultVatType, saveVatType]);

  const setToDefaultDeliveryMethod = useCallback(() => {
    resetVatType();
    resetTableNumber();
  }, [resetVatType]);

  const resetTableNumber = () => {
    setTableNumber(undefined);
  };

  const handleSetTableNumber = useCallback(
    (tableNumber: string) => {
      setTableNumber(tableNumber);
    },
    [setTableNumber]
  );

  const handleSetVatType = useCallback(
    (vatType: 'EAT_IN' | 'TAKEAWAY') => {
      saveVatType(vatType);
    },
    [saveVatType]
  );

  const deliveryFee = useDeliveryFee(deliveryMethod, tableNumber);

  return useMemo(
    () => ({
      deliveryFee,
      comment: comment,
      setComment: saveComment,
      vatType: vatType,
      setVatType: handleSetVatType,
      tableNumber,
      setTableNumber: handleSetTableNumber,
      setToDefaultDeliveryMethod,
      resetTableNumber,
      deliveryMethod,
      selectedPaymentMethod,
      setSelectedPaymentMethod,
      dynamicFieldValues,
      setDynamicFieldValues,
      setSidebarContent,
      showSelectTable: sidebarContent === 'SELECT_TABLE',
      showPaymentDialog: sidebarContent === 'PAYMENT',
      showCreateTableDialog: sidebarContent === 'CREATE_TABLE',
      showSetTableNumber: sidebarContent === 'SET_TABLE_NUMBER',
    }),
    [
      comment,
      vatType,
      deliveryFee,
      deliveryMethod,
      dynamicFieldValues,
      handleSetTableNumber,
      handleSetVatType,
      saveComment,
      selectedPaymentMethod,
      setToDefaultDeliveryMethod,
      sidebarContent,
      tableNumber,
    ]
  );
});

export { CheckoutContextProvider, useCheckout };
